import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from "react";
import {  ActiveSubscriptionUsageResponseModel, BillingInstanceUsageType, ProductType } from "../swagger-clients/s365-dashboard-v2-api-clients.service";
import { getLicensingClient, getSubscriptionsClient } from "../services/dashboard.service";
import { useIsAuthenticated } from "@azure/msal-react";


type DwsimProLicenseContextType = {
    activeSubscription?: ActiveSubscriptionUsageResponseModel;
    isLoading: boolean;
    isValidLicense: boolean;
    getActiveSubscription: () => Promise<ActiveSubscriptionUsageResponseModel>;
    reloadLicense: () => Promise<void>;
};

const initialState = {
    activeSubscription: undefined,
    isLoading: false,
    isValidLicense: false,
    getActiveSubscription: () => { throw "getActiveSubscription() not initialized."; },
    reloadLicense: () => { throw "reloadLicense() not initialized."; }
};
const DwsimProLicenseContext = createContext<DwsimProLicenseContextType>(initialState);

type DwsimProLicenseProviderProps = {
    children: ReactNode;
};

export const DwsimProLicenseProvider = ({ children }: DwsimProLicenseProviderProps) => {
    const [subscription, setSubscription] = useState<ActiveSubscriptionUsageResponseModel>();
    const [isValidLicense, setIsValidLicense] = useState<boolean>();
    const isAuthenticated = useIsAuthenticated();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const reloadLicense = useCallback(async () => {
        if (!isAuthenticated) {
            setSubscription(undefined);
            return;
        }
        const subscriptionResp = await getActiveSubscription();
       // console.log("Fetched subscription:", subscriptionResp);
        setSubscription(subscriptionResp);

    }, [isAuthenticated]);

    useEffect(() => {
        const isValid = !!subscription && (subscription.usageType == BillingInstanceUsageType.Unlimited || subscription.allowedTokens > subscription.usedTokens);
        setIsValidLicense(isValid);
    }, [subscription]);

    const getActiveSubscription = async () => {
        try {
            setIsLoading(true);
            const client = getSubscriptionsClient();
            const subscriptionResp = await client.getActiveSubscription(ProductType.DwsimPro);
            return subscriptionResp;

        } catch (error) {
            console.error("Error fetching license data:", error);
            return null;
        }
        finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        // Start interval only after user is authenticated
        if (isAuthenticated) {

            reloadLicense();

            const interval = setInterval(async () => {
                await reloadLicense();
            }, 30 * 1000);

            return () => clearInterval(interval); // Cleanup on unmount
        }
    }, [reloadLicense, isAuthenticated]);    

    const value = {
        activeSubscription: subscription,
        isLoading: isLoading,
        isValidLicense: isValidLicense,
        getActiveSubscription: getActiveSubscription,
        reloadLicense: reloadLicense
    } as DwsimProLicenseContextType;

    return (
        <DwsimProLicenseContext.Provider value={value}>
            {children}
        </DwsimProLicenseContext.Provider>
    );
};

export const useDwsimProLicense = () => {
    const context = useContext(DwsimProLicenseContext);
    if (!context) {
        throw new Error("useDwsimProLicense must be used within a DwsimProLicenseProvider");
    }
    return context;
};